import moment from 'moment';

import { PRODUCT_EXPIRY_CHECK_SCHEDULE_TYPE } from '../../constants';
import { getLocalTime } from '../time/time';
import { countProductsToRemoveOnNextService } from './helpers';

export const toProductExpiryScheduleId = (value) => {
	if (!value) {
		return null;
	}

	if (typeof value === 'string') {
		return value;
	}

	if (value._id) {
		return value._id.toString();
	}

	if (value.id) {
		return value.id.toString();
	}

	if (typeof value.toString === 'function') {
		return value.toString();
	}

	return null;
};

const getDateOrNull = (value) => {
	if (!value) {
		return null;
	}

	const parsedDate = value instanceof Date ? value : new Date(value);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getScheduleDayMoment = (value, timezone) => {
	const parsedDate = getDateOrNull(value);
	if (!parsedDate) {
		return null;
	}

	return timezone
		? getLocalTime(timezone, parsedDate).startOf('day')
		: moment(parsedDate).startOf('day');
};

export const getProductExpiryScheduleRepeatInterval = (schedule = {}) => {
	const repeatInterval = Number(schedule.repeatInterval);
	return Number.isInteger(repeatInterval) && repeatInterval > 0 ? repeatInterval : 0;
};

export const getProductExpiryScheduleEffectiveStartAt = (schedule = {}) => {
	return getDateOrNull(schedule.startsAt) || getDateOrNull(schedule.created);
};

export const getProductExpiryScheduleIntervalWindow = ({
	schedule,
	referenceDate,
	timezone,
}) => {
	const startDay = getScheduleDayMoment(getProductExpiryScheduleEffectiveStartAt(schedule), timezone);
	const referenceDay = getScheduleDayMoment(referenceDate || new Date(), timezone);
	if (!startDay?.isValid() || !referenceDay?.isValid() || referenceDay.isBefore(startDay, 'day')) {
		return null;
	}

	const repeatInterval = getProductExpiryScheduleRepeatInterval(schedule);
	if (!repeatInterval) {
		return null;
	}
	const elapsedDays = referenceDay.diff(startDay, 'days');
	const intervalStart = startDay.clone().add(
		Math.floor(elapsedDays / repeatInterval) * repeatInterval,
		'days',
	);

	return {
		start: intervalStart,
		end: intervalStart.clone().add(repeatInterval, 'days'),
	};
};

export const hasProductExpiryScheduleStarted = ({
	schedule,
	referenceDate,
	timezone,
}) => {
	const effectiveStartAt = getProductExpiryScheduleEffectiveStartAt(schedule);
	if (!effectiveStartAt) {
		return true;
	}

	const startDay = getScheduleDayMoment(effectiveStartAt, timezone);
	const referenceDay = getScheduleDayMoment(referenceDate || new Date(), timezone);
	return Boolean(startDay?.isValid() && referenceDay?.isValid() && !referenceDay.isBefore(startDay, 'day'));
};

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

const getPositiveNumber = (value) => {
	const number = Number(value);
	return Number.isFinite(number) && number > 0 ? number : 0;
};

const getItemQuantity = (item = {}) => {
	return getPositiveNumber(item.presentProductQuantity ?? item.quantity);
};

const getItemVendRate = ({ item = {}, planogramItemsVendRate = {} }) => {
	const ownVendRate = Number(item.planogramItemVendRate);
	if (Number.isFinite(ownVendRate)) {
		return ownVendRate;
	}

	const planogramItemId = toProductExpiryScheduleId(item.planogramItemId);
	const vendRate = Number(planogramItemsVendRate?.[planogramItemId]);
	return Number.isFinite(vendRate) ? vendRate : 0;
};

const getExpiryDateValues = ({ item = {} }) => {
	if (item.hasExpiryDates === false || !hasOwn(item, 'expiryDates')) {
		return null;
	}

	const quantity = getItemQuantity(item);
	const expiryDates = item.expiryDates || {};
	if (item.isMarket && expiryDates[1]) {
		return [expiryDates[1]];
	}
	if (!quantity) {
		return [];
	}

	if (item.isMarket) {
		return [expiryDates?.[1] || null];
	}

	return [...Array(quantity)].map((_, index) => expiryDates?.[index + 1] || null);
};

const getEarliestKnownExpiryDate = (expiryDateValues = []) => {
	const timestamps = expiryDateValues
		.map(getDateOrNull)
		.filter(Boolean)
		.map(date => date.getTime())
		.sort((left, right) => left - right);

	return timestamps.length ? new Date(timestamps[0]) : null;
};

const getItemLastCheckedAt = ({ item = {}, scheduleId }) => {
	if (!scheduleId) {
		return null;
	}

	return getDateOrNull(item.productExpiryScheduleLastCheckedAt?.[scheduleId]);
};

const getItemLastProductChangeAt = (item = {}) => {
	return getDateOrNull(item.lastProductChangeTime || item.planogramItemStatistics?.lastProductChangeTime);
};

export const getProductExpiryScheduleItemAdaptiveDueDetails = ({
	schedule,
	item = {},
	scheduleId: rawScheduleId,
	plannedDate,
	expiryDateThresholdDays,
	averageDaysSinceLastService,
	planogramItemsVendRate,
	timezone,
}) => {
	if (!schedule?.adaptiveExpiryChecksEnabled || schedule?.type !== PRODUCT_EXPIRY_CHECK_SCHEDULE_TYPE.FLEXIBLE) {
		return { due: false, reason: null };
	}

	if (!hasProductExpiryScheduleStarted({ schedule, referenceDate: plannedDate, timezone })) {
		return { due: false, reason: null };
	}

	const scheduleId = rawScheduleId || toProductExpiryScheduleId(schedule);
	const lastCheckedAt = getItemLastCheckedAt({ item, scheduleId });
	const lastProductChangeAt = getItemLastProductChangeAt(item);

	if (lastCheckedAt && lastProductChangeAt && moment(lastProductChangeAt).isAfter(moment(lastCheckedAt))) {
		return { due: true, reason: 'productChangedAfterCheck' };
	}

	const expiryDateValues = getExpiryDateValues({ item });
	if (!expiryDateValues) {
		return { due: false, reason: null };
	}

	if (expiryDateValues.length && expiryDateValues.some(value => !value)) {
		return { due: true, reason: 'missingExpiryDate' };
	}

	const plannedMoment = getScheduleDayMoment(plannedDate || new Date(), timezone);
	const safePlannedMoment = plannedMoment?.isValid()
		? plannedMoment
		: getScheduleDayMoment(new Date(), timezone);
	const earliestKnownExpiryDate = getEarliestKnownExpiryDate(expiryDateValues);
	if (!earliestKnownExpiryDate) {
		return { due: false, reason: null };
	}

	const earliestKnownExpiryMoment = getScheduleDayMoment(earliestKnownExpiryDate, timezone);
	const daysUntilEarliestExpiry = earliestKnownExpiryMoment.diff(safePlannedMoment, 'days');
	if (daysUntilEarliestExpiry <= 0) {
		return { due: true, reason: 'expiredOrExpiresToday' };
	}

	const thresholdDays = Number(expiryDateThresholdDays);
	const serviceBaselineDays = getPositiveNumber(averageDaysSinceLastService);
	const hasServiceBaseline = serviceBaselineDays > 0;
	if (item.isMarket) {
		// Market customers can reorder products, so vending-style linear sell-through
		// cannot prove that the oldest recorded stock will sell before expiry.
		const marketReviewWindowDays = Math.max(
			serviceBaselineDays,
			Number.isFinite(thresholdDays) && thresholdDays >= 0 ? thresholdDays : 0,
		);
		if (marketReviewWindowDays > 0 && daysUntilEarliestExpiry <= marketReviewWindowDays) {
			return { due: true, reason: 'marketExpiryInsideReviewWindow' };
		}
	}
	else {
		const vendRate = getItemVendRate({ item, planogramItemsVendRate });
		const productsToRemove = countProductsToRemoveOnNextService(
			vendRate,
			averageDaysSinceLastService,
			item.expiryDates || {},
			{ referenceDate: plannedDate, timezone },
		);
		if (productsToRemove > 0) {
			return { due: true, reason: 'mayExpireBeforeNextService', productsToRemove };
		}
	}

	if (!hasServiceBaseline && Number.isFinite(thresholdDays) && thresholdDays >= 0 && daysUntilEarliestExpiry <= thresholdDays) {
		return { due: true, reason: 'expiresSoonWithoutServiceBaseline' };
	}

	return { due: false, reason: null };
};

export const getProductExpiryScheduleItemIntervalDueDetails = ({
	schedule,
	item = {},
	scheduleId: rawScheduleId,
	plannedDate,
	timezone,
}) => {
	if (schedule?.type && schedule.type !== PRODUCT_EXPIRY_CHECK_SCHEDULE_TYPE.FLEXIBLE) {
		return { due: false, reason: null };
	}

	if (!hasProductExpiryScheduleStarted({ schedule, referenceDate: plannedDate, timezone })) {
		return { due: false, reason: null };
	}

	const repeatInterval = getProductExpiryScheduleRepeatInterval(schedule);
	if (!repeatInterval) {
		return { due: false, reason: null };
	}
	const plannedMoment = getScheduleDayMoment(plannedDate || new Date(), timezone);
	if (!plannedMoment?.isValid()) {
		return { due: false, reason: null };
	}

	const scheduleId = rawScheduleId || toProductExpiryScheduleId(schedule);
	const lastCheckedAt = getItemLastCheckedAt({ item, scheduleId });
	if (!lastCheckedAt) {
		return { due: true, reason: 'neverChecked' };
	}

	const nextCheckMoment = getScheduleDayMoment(lastCheckedAt, timezone)?.add(repeatInterval, 'days');
	if (!nextCheckMoment.isValid() || plannedMoment.isSameOrAfter(nextCheckMoment)) {
		return { due: true, reason: 'repeatIntervalElapsed' };
	}

	return { due: false, reason: null };
};

export const getProductExpiryScheduleItemDueDetails = (options) => {
	const adaptiveDueDetails = getProductExpiryScheduleItemAdaptiveDueDetails(options);
	if (adaptiveDueDetails.due) {
		return adaptiveDueDetails;
	}

	return getProductExpiryScheduleItemIntervalDueDetails(options);
};
